/**
 * Timeslot v2 매트릭스 — web 병합/최저가 헬퍼 (Phase B-2)
 *
 * ⚠️ locarora-admin lib/utils/timeslot-matrix.ts 의 mergeTimeslotMatrixConfig
 * 계약을 web 관례로 이식한 복제본 (레포 분리 — admin 파일 직접 import 금지).
 * 계약 변경 시 양쪽 동기 필수:
 * - 언어 폴백: 요청 언어 행 → 원본 언어 행 → display_order당 첫 행
 * - display_order 정렬 (교차 언어 정체성 키)
 * - prices 매트릭스 재조립: (sub_display_order × option_display_order) →
 *   subProducts[].prices[] (열 순서 = 정렬된 timeOptions의 실제 display_order, 누락 칸 = 0)
 */

/** get_product_detail_full 신규 3키 행 형태 (전 언어 행) */
export interface TimeOptionRpcRow {
  id?: string;
  display_order: number;
  lang_code?: string | null;
  label: string | null;
  /** v2 자동 완료용 운영 시간 (07-27) — row_to_json이라 RPC 수정 없이 자동 포함 */
  duration_hours?: number | null;
}

export interface SubProductRpcRow {
  id?: string;
  display_order: number;
  lang_code?: string | null;
  name: string | null;
  description?: string | null;
  total_stock?: number | null;
  /** 세부 상품별 주문당 최대 구매 수량 — null = 무제한 (08-10, 언어별 행 동일값) */
  max_purchase_quantity?: number | null;
}

export interface SubProductPriceRpcRow {
  sub_display_order: number;
  option_display_order: number;
  price: number | string; // numeric은 드라이버에 따라 string일 수 있음
}

export interface MergedTimeOption {
  id: string;
  label: string;
  /** 언어 무관 정체성 키 — 예약 스냅샷(option_display_order)·가용성 매칭에 사용 */
  displayOrder: number;
  /** v2 자동 완료용 운영 시간 통과 (07-27) — 화면/가격 무관, 값 보존 캐리 */
  durationHours: number | null;
}

export interface MergedSubProduct {
  id: string;
  name: string;
  description: string;
  /** null = 무제한 */
  totalStock: number | null;
  /** 세부 상품별 주문당 최대 구매 수량 — null = 무제한 (08-10) */
  maxPurchaseQuantity: number | null;
  /** prices[i] = timeOptions[i]에 대한 가격 */
  prices: number[];
  /** 언어 무관 정체성 키 — 예약 스냅샷(sub_display_order)·가용성 매칭에 사용 */
  displayOrder: number;
}

/**
 * v2 최저가 단일 소스 (Phase B-2) — 가격 매트릭스 셀 중 0 초과 최소값.
 * 카드/검색(calculateMinPrice 폴백)·PricingInfo·JSON-LD·convertedPrice 전부 이 함수 경유.
 */
export function getTimeslotV2MinPrice(
  subProductPrices?: Array<{ price: number | string }> | null,
): number | null {
  if (!subProductPrices || subProductPrices.length === 0) return null;
  let min = Infinity;
  for (const cell of subProductPrices) {
    const price = Number(cell.price);
    if (Number.isFinite(price) && price > 0 && price < min) {
      min = price;
    }
  }
  return min === Infinity ? null : min;
}

/**
 * v2 재가격 단일 소스 (Phase B-4) — (sub_display_order × option_display_order)
 * 셀에서 서버 권위 단가 확정. 셀 부재 = null (호출측 fail-closed: 결제 거부).
 * checkout.ts / cart.ts / Edge authoritative-pricing(Deno 미러 쌍둥이) 전부 이 규칙.
 * ⚠️ 수정 시 Edge _shared/authoritative-pricing.ts의 미러도 함께 갱신.
 */
export function resolveTimeslotV2UnitPrice(
  cells: Array<{
    sub_display_order: number;
    option_display_order: number;
    price: number | string;
  }>,
  subDisplayOrder: number,
  optionDisplayOrder: number,
): number | null {
  const cell = cells.find(
    (c) =>
      c.sub_display_order === subDisplayOrder &&
      c.option_display_order === optionDisplayOrder,
  );
  if (!cell) return null;
  const price = Number(cell.price);
  return Number.isFinite(price) ? price : null;
}

/**
 * 이용 시간 칩 "긴 라벨" 임계 (07-17 사용자 확정, 목업
 * locarora-admin/docs/preview-chip-long-labels.html A안) — 옵션 라벨 중
 * 하나라도 trim 후 12자 초과면 칩을 풀폭 세로 스택으로 전환.
 * ⚠️ admin lib/utils/timeslot-matrix.ts의 미러와 동일 값·규칙 동기 유지.
 */
export const LONG_LABEL_THRESHOLD = 12;

export function hasLongTimeOptionLabel(
  labels: Array<string | null | undefined>,
): boolean {
  return labels.some((l) => (l ?? "").trim().length > LONG_LABEL_THRESHOLD);
}

/** PricingInfo v2 요금 상세 그룹 행 (07-17 A안) */
export interface TimeslotV2PriceGroupRow {
  key: string;
  /** 옵션 라벨만 (상품명은 그룹 헤더가 담당) — 빈 라벨은 #n 폴백 */
  label: string;
  price: number;
}

export interface TimeslotV2PriceGroup {
  id: string;
  /**
   * 그룹 헤더(세부상품명) — null = 헤더 생략:
   * 노출 그룹이 1개(마이그레이션 상품 = v1 모양)이거나,
   * 세부상품명이 공백/상품명과 동일(중복 생략 규칙, 07-17)일 때.
   */
  name: string | null;
  rows: TimeslotV2PriceGroupRow[];
}

/**
 * PricingInfo v2 요금 상세 — 세부 상품별 그룹 리스트 (07-17 사용자 확정 A안,
 * 목업: locarora-admin/docs/preview-pricing-info.html).
 * - >0 셀만 (기존 v2Tiers 필터 유지), 빈 그룹(전 셀 0)은 제외
 * - 그룹 순서 = subProducts 입력 순서 유지 (merge에서 이미 displayOrder 정렬)
 * - 헤더 생략 규칙은 TimeslotV2PriceGroup.name 참조
 */
export function buildTimeslotV2PriceGroups(
  matrix: {
    timeOptions: Array<{ id: string; label: string }>;
    subProducts: Array<{ id: string; name: string; prices: number[] }>;
  },
  productName?: string,
): TimeslotV2PriceGroup[] {
  if (matrix.timeOptions.length === 0) return [];
  const groups = matrix.subProducts
    .map((sp) => ({
      id: sp.id,
      rawName: sp.name.trim(),
      rows: sp.prices
        .map((price, optIdx) => ({ price, optIdx }))
        .filter(({ price }) => price > 0)
        .map(({ price, optIdx }) => ({
          key: `${sp.id}-${matrix.timeOptions[optIdx]?.id ?? optIdx}`,
          label: matrix.timeOptions[optIdx]?.label || `#${optIdx + 1}`,
          price,
        })),
    }))
    .filter((g) => g.rows.length > 0);
  const single = groups.length === 1;
  const product = productName?.trim();
  return groups.map(({ id, rawName, rows }) => ({
    id,
    name:
      single || !rawName || (product != null && rawName === product)
        ? null
        : rawName,
    rows,
  }));
}

/**
 * 언어 폴백 행 선택 — 요청 언어 → 원본 언어 → display_order당 첫 행 (혼합 언어 안전).
 *
 * 08-17: 일일대여 v2(`daily-matrix.ts`)가 **같은 세부 상품 행**을 병합하므로 모듈
 * 스코프로 승격해 공유한다(동작 불변 — 기존 mergeTimeslotMatrixConfig 내부 클로저를
 * 그대로 꺼낸 것). 규칙이 갈라지면 같은 상품이 모드에 따라 다른 언어 행을 고른다.
 */
export function pickLangRowsByDisplayOrder<
  T extends { display_order: number; lang_code?: string | null },
>(allRows: T[], lang: string, originalLang: string): T[] {
  const byLang = (l: string) =>
    allRows.filter((r) => (r.lang_code || "ko") === l);
  const langRows = byLang(lang);
  if (langRows.length > 0) return langRows;
  const originalRows = byLang(originalLang);
  if (originalRows.length > 0) return originalRows;
  const seen = new Set<number>();
  return allRows.filter((r) => {
    if (seen.has(r.display_order)) return false;
    seen.add(r.display_order);
    return true;
  });
}

/**
 * 언어 병합 — RPC 3키(전 언어 행)를 특정 언어의 매트릭스 구조로 재조립.
 * (admin mergeTimeslotMatrixConfig와 동일 계약)
 */
export function mergeTimeslotMatrixConfig(
  rows: {
    timeOptionRows: TimeOptionRpcRow[];
    subProductRows: SubProductRpcRow[];
    priceRows: SubProductPriceRpcRow[];
  },
  lang: string,
  originalLang: string,
): {
  timeOptions: MergedTimeOption[];
  subProducts: MergedSubProduct[];
} {
  const pickLangRows = <
    T extends { display_order: number; lang_code?: string | null },
  >(
    allRows: T[],
  ): T[] => pickLangRowsByDisplayOrder(allRows, lang, originalLang);

  const sortedOptions = pickLangRows(rows.timeOptionRows).sort(
    (a, b) => a.display_order - b.display_order,
  );
  const timeOptions = sortedOptions.map((row, index) => ({
    id: row.id || `topt-${row.display_order ?? index}`,
    label: row.label || "",
    displayOrder: row.display_order ?? index,
    durationHours: row.duration_hours ?? null,
  }));
  // 열 순서 = 정렬된 옵션의 실제 display_order (배열 인덱스가 아님 — 구멍 안전)
  const optionOrders = sortedOptions.map((row) => row.display_order);

  const priceMap = new Map<string, number>();
  for (const row of rows.priceRows) {
    priceMap.set(
      `${row.sub_display_order}-${row.option_display_order}`,
      Number(row.price) || 0,
    );
  }

  const subProducts = pickLangRows(rows.subProductRows)
    .sort((a, b) => a.display_order - b.display_order)
    .map((row, index) => ({
      id: row.id || `subp-${row.display_order ?? index}`,
      name: row.name || "",
      description: row.description || "",
      totalStock: row.total_stock ?? null,
      maxPurchaseQuantity: row.max_purchase_quantity ?? null,
      prices: optionOrders.map(
        (optionOrder) =>
          priceMap.get(`${row.display_order}-${optionOrder}`) ?? 0,
      ),
      displayOrder: row.display_order ?? index,
    }));

  return { timeOptions, subProducts };
}
